import { LoanDashboardFormCard } from "../LoanDashboardForm";
import { formatAmount, getLoanCompletionPercentage } from "../../../utils";
import {
  LoanApplication,
  LoanRequestPurpose,
  LOSPlatform,
} from "../../../contexts";
import { FC, useMemo } from "react";
import {
  LoanApplicationCaseStatus,
  LoanApplicationCaseStatusDashboardMappingToText,
  LoanApplicationStatus,
  LoanApplicationStatusDashboardMappingToText,
  LoanProductType,
} from "../../../hooks/api/types";
import { LoanDashboardProgress } from "../LoanDashboardProgress";
import { getLoanInputFromApplication } from "../../../utils/loanUtils";

type LoanDetailsProps = {
  loan: LoanApplication;
};

export const LoanDetails: FC<LoanDetailsProps> = ({ loan }) => {
  const {
    paidAmount,
    remainingAmount,
    lateFee,
    hasRepaymentData,
    availableAmount,
  } = useMemo(() => {
    return {
      paidAmount: loan.paidAmount ?? 0,
      remainingAmount: loan.remainingAmount ?? 0,
      lateFee: loan?.lateFee ?? "-",
      hasRepaymentData:
        loan.paidAmount !== null &&
        loan.paidAmount !== undefined &&
        loan.remainingAmount !== null &&
        loan.remainingAmount !== undefined,
      availableAmount:
        loan?.availableAmount ?? loan?.creditLine?.availableAmount,
    };
  }, [
    loan?.amount,
    loan?.paidAmount,
    loan?.remainingAmount,
    loan?.lateFee,
    loan?.availableAmount,
    loan?.creditLine?.availableAmount,
  ]);

  const completionPercentage = useMemo(
    () => getLoanCompletionPercentage(paidAmount, remainingAmount),
    [paidAmount, remainingAmount]
  );

  const outstandingPercentage = 100 - completionPercentage;

  const loanInput = useMemo(() => getLoanInputFromApplication(loan), [loan]);
  const loanAmount = useMemo(() => {
    if (
      loan?.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
      loan?.loanType === LoanProductType.LOC_PAYABLE_FINANCING
    ) {
      return loan?.creditLine?.requestedLimit;
    }
    if (
      (loan?.approvedAmount || loan?.amount) &&
      loan?.losPlatform === LOSPlatform.CREDIBLEX
    ) {
      return loan?.approvedAmount ? loan.approvedAmount : loan.amount;
    }
    return loanInput?.loanAmount;
  }, [loan, loanInput]);

  const showProgress = useMemo(() => {
    return (
      loan.status === LoanApplicationStatus.ACTIVE ||
      loan.status === LoanApplicationStatus.PARTIALLY_DISBURSED ||
      loan.caseStatus === LoanApplicationCaseStatus.ACTIVE ||
      loan.caseStatus === LoanApplicationCaseStatus.RESTRUCTURED
    );
  }, [loan]);

  const showAvailable = useMemo(() => {
    return (
      loan.status === LoanApplicationStatus.WAITING_FOR_DISBURSEMENT ||
      loan.caseStatus === LoanApplicationCaseStatus.WAITING_FOR_DISBURSEMENT ||
      loan.caseStatus === LoanApplicationCaseStatus.SEND_TO_SERVICING
    );
  }, [loan.status, loan.caseStatus]);

  const progressConfig = useMemo(() => {
    if (loan.requestPurpose === LoanRequestPurpose.CREDIT_LINE) {
      return { value: completionPercentage, label: "Complete" };
    }

    const isNonCrediblexActive =
      loan.losPlatform !== LOSPlatform.CREDIBLEX &&
      (loan.status === LoanApplicationStatus.ACTIVE ||
        loan.status === LoanApplicationStatus.PARTIALLY_DISBURSED);
    const isRestructured =
      loan.caseStatus === LoanApplicationCaseStatus.RESTRUCTURED;

    if (isNonCrediblexActive || isRestructured) {
      return { value: completionPercentage, label: "Available" };
    }

    return { value: outstandingPercentage, label: "Outstanding" };
  }, [
    loan.requestPurpose,
    completionPercentage,
    outstandingPercentage,
    loan.losPlatform,
    loan.status,
    loan.caseStatus,
  ]);

  return (
    <div className="sm:cx-flex cx-flex-wrap cx-items-center cx-py-4 cx-px-2 sm:cx-gap-x-4 sm:cx-divide-x sm:cx-divide-neutral-light">
      <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
        <LoanDashboardFormCard
          label={
            loan.losPlatform === LOSPlatform.CREDIBLEX && loan.caseStatus
              ? LoanApplicationCaseStatusDashboardMappingToText[loan.caseStatus]
              : LoanApplicationStatusDashboardMappingToText[loan.status] ??
                "Requested Amount"
          }
        >
          <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
            <div className="cx-text-text-primary cx-font-medium cx-text-xl">
              <div className="cx-font-aed-font"> D </div>
            </div>
            <div className="cx-text-text-primary cx-font-bold cx-text-xl">
              {formatAmount(loanAmount * (loanInput?.discountRate ?? 1))}
            </div>
          </div>
        </LoanDashboardFormCard>
      </div>

      {showProgress && (
        <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
          <LoanDashboardFormCard label="Outstanding amount">
            <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
              <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                <div className="cx-font-aed-font"> D </div>
              </div>
              <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                {hasRepaymentData ? formatAmount(remainingAmount) : "-"}
              </div>
            </div>
          </LoanDashboardFormCard>
        </div>
      )}

      {loan.status !== LoanApplicationStatus.ACTIVE &&
        loan.status !== LoanApplicationStatus.PARTIALLY_DISBURSED &&
        loan.caseStatus !== LoanApplicationCaseStatus.ACTIVE &&
        loan.caseStatus !== LoanApplicationCaseStatus.EARLY_CLOSURE &&
        loan.caseStatus !== LoanApplicationCaseStatus.FORCED_CLOSURE && (
          <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
            <LoanDashboardFormCard label="Duration">
              <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
                <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                  {loanInput?.duration}
                </div>
                <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                  {loanInput?.durationType}
                </div>
              </div>
            </LoanDashboardFormCard>
          </div>
        )}

      {showProgress &&
        lateFee != null &&
        loan.caseStatus == LoanApplicationCaseStatus.PAST_MATURITY && (
          <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
            <LoanDashboardFormCard label="Late fee">
              <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
                <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                  <div className="cx-font-aed-font"> D </div>
                </div>
                <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                  {lateFee != "-" ? formatAmount(lateFee as number) : "-"}
                </div>
              </div>
            </LoanDashboardFormCard>
          </div>
        )}

      {showProgress && (
        <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-justify-center cx-py-3 cx-px-4">
          <LoanDashboardProgress
            value={hasRepaymentData ? progressConfig.value : 0}
            valueText={hasRepaymentData ? undefined : "-"}
            label={progressConfig.label}
          />
        </div>
      )}

      {showAvailable && (
        <div className="cx-flex-1 cx-flex cx-flex-col cx-items-center cx-py-3 cx-px-4">
          <LoanDashboardFormCard label="Available amount">
            <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
              <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                <div className="cx-font-aed-font"> D </div>
              </div>
              <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                {availableAmount != null ? formatAmount(availableAmount) : "-"}
              </div>
            </div>
          </LoanDashboardFormCard>
        </div>
      )}
    </div>
  );
};

export default LoanDetails;
